import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  TAROT_COURSE_TITLE,
  TAROT_COURSE_INTRO,
  tarotCourseNav,
  tarotCourseSections,
  TAROT_COURSE_LEAF_IDS,
  MINOR_ARCANA_CHILD_IDS,
  TAROT_COURSE_ID_MIGRATION
} from '../../data/tarotCourseContent.fi';
import { getCardImageUrl } from '../../data/tarotCards';
import { getMajorCards, getMinorCardsBySuit, getDisplayCard, allCards, getCardById } from '../../data/tarotCards.fi';
import { getCardById as getGeneratedCardById } from '../../data/tarotCardsGenerated';
import { getCardDescription } from '../../data/tarotCardDescriptions.fi';
import { SpreadDiagramByIndex } from '../../components/tarot/SpreadDiagramsClean';
import '../Tarot.css';
import './TarotCoursePage.css';

const SHOW_EXERCISES = false;
const SHOW_PROGRESS = false;

const PROGRESS_KEY = 'amm_course_tarot_progress_v1';
function loadProgress() {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    const migrated = arr.map(id => (TAROT_COURSE_ID_MIGRATION[id] != null ? TAROT_COURSE_ID_MIGRATION[id] : id));
    return [...new Set(migrated)].filter(id => typeof id === 'string' && TAROT_COURSE_LEAF_IDS.includes(id));
  } catch {
    return [];
  }
}
function saveProgress(completedIds) {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(completedIds));
  } catch (e) {
    console.warn('Tarot course progress save failed', e);
  }
}

const MAJOR_ARCANA_ROMAN = ['0', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX', 'XXI'];

function majorArcanaNumberLabel(num) {
  if (num == null || num < 0 || num > 21) return '';
  return MAJOR_ARCANA_ROMAN[num] || String(num);
}

function formatMajorArcanaTitle(number, nameFi, nameEn) {
  const numLabel = majorArcanaNumberLabel(number);
  if (!numLabel || nameFi == null) return nameFi || '';
  return nameEn ? `${numLabel}. ${nameFi} (${nameEn})` : `${numLabel}. ${nameFi}`;
}

const SUIT_GENITIVE = { wands: 'Sauvojen', cups: 'Maljojen', swords: 'Miekkojen', pentacles: 'Lanttien' };
const RANK_FI = {
  ace: 'Ässä', '02': 'Kaksi', '03': 'Kolme', '04': 'Neljä', '05': 'Viisi',
  '06': 'Kuusi', '07': 'Seitsemän', '08': 'Kahdeksan', '09': 'Yhdeksän', '10': 'Kymmenen',
  page: 'Lähetti', knight: 'Ritari', queen: 'Kuningatar', king: 'Kuningas'
};
function formatMinorArcanaTitle(suit, rankKey, nameEn) {
  const suitGen = suit && SUIT_GENITIVE[suit];
  const rankFi = rankKey != null && RANK_FI[rankKey];
  if (!suitGen || !rankFi) return nameEn || '';
  return nameEn ? `${suitGen} ${rankFi} (${nameEn})` : `${suitGen} ${rankFi}`;
}

function CardDetailModal({ card, orientation, onClose, generatedCard }) {
  const [orient, setOrient] = React.useState(orientation || 'upright');
  if (!card) return null;
  const isMinor = card.isMinor === true;
  const imageUrl = card.imageUrl != null ? card.imageUrl : (isMinor ? null : getCardImageUrl(card.name));
  const interp = !isMinor && (orient === 'reversed' ? card.reversed : card.upright);
  const keywords = (interp && interp.keywords) ? interp.keywords.join(', ') : '';
  const content = card.content;
  const fallbackDesc = !content ? getCardDescription(card) : null;

  return (
    <div className="tarot-course-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Kortin tiedot">
      <div className="tarot-course-modal card" onClick={e => e.stopPropagation()}>
        <button type="button" className="tarot-course-modal-close" onClick={onClose} aria-label="Sulje">×</button>
        {imageUrl && (
          <div className="card-image-container">
            <img
              src={imageUrl}
              alt={!isMinor && card.number != null && card.name ? formatMajorArcanaTitle(card.number, card.nameFi, card.name) : (isMinor ? formatMinorArcanaTitle(card.suit, card.rankKey, card.name) : (card.nameFi || 'Tarot-kortti'))}
              className={`tarot-card-image ${!isMinor && orient === 'reversed' ? 'reversed' : ''}`}
            />
          </div>
        )}
        <div className="card-header tarot-course-modal-header">
          {!isMinor && card.number != null && (
            <div className="tarot-course-modal-badge" aria-hidden>
              {majorArcanaNumberLabel(card.number)}
            </div>
          )}
          <h3 className="card-name">
            {!isMinor && card.number != null && card.name
              ? formatMajorArcanaTitle(card.number, card.nameFi, card.name)
              : (isMinor ? formatMinorArcanaTitle(card.suit, card.rankKey, card.name) : (card.nameFi || ''))}
          </h3>
          {!isMinor && (
            <>
              <div className="card-orientation">
                {orient === 'upright' ? 'Oikeinpäin' : 'Käänteinen'}
              </div>
              <div className="tarot-course-orientation-toggle">
                <button type="button" className="btn btn-secondary" onClick={() => setOrient('upright')}>Oikeinpäin</button>
                <button type="button" className="btn btn-secondary" onClick={() => setOrient('reversed')}>Käänteinen</button>
              </div>
            </>
          )}
        </div>
        {generatedCard && (generatedCard.uprightFi || generatedCard.reversedFi) && (
          <div className="tarot-course-modal-tulkinta">
            <h4 className="tarot-course-modal-tulkinta-title">Tulkinta</h4>
            <div className="tarot-course-meaning-block tarot-course-upright-block">
              <h5 className="tarot-course-meaning-label">Pysty</h5>
              {generatedCard.uprightFi && <p className="tarot-course-meaning-fi">{generatedCard.uprightFi}</p>}
            </div>
            <div className="tarot-course-meaning-block tarot-course-reversed-block">
              <h5 className="tarot-course-meaning-label">Käänteinen</h5>
              {generatedCard.reversedFi && <p className="tarot-course-meaning-fi">{generatedCard.reversedFi}</p>}
            </div>
          </div>
        )}
        {content && (content.imagery || content.coreMeaning) && (
          <div className="card-interpretation tarot-course-desc">
            {content.imagery && <p><strong>Kuvailu / symboliikka:</strong> {content.imagery}</p>}
            {content.coreMeaning && <p><strong>Ydinmerkitys:</strong> {content.coreMeaning}</p>}
            {content.shadow && <p><strong>Varjo / haaste:</strong> {content.shadow}</p>}
            {content.readingThemes && content.readingThemes.length > 0 && (
              <p><strong>Lukutilanteissa:</strong> {content.readingThemes.join(', ')}</p>
            )}
            {content.notes && <p className="tarot-course-reflection"><strong>Pohdintakysymys:</strong> {content.notes}</p>}
          </div>
        )}
        {!content && fallbackDesc && (
          <div className="card-interpretation tarot-course-desc">
            {fallbackDesc.imagery && <p><strong>Kuva:</strong> {fallbackDesc.imagery}</p>}
            <p><strong>Ydinmerkitys:</strong> {fallbackDesc.meaning}</p>
            {fallbackDesc.shadow && <p><strong>Varjo:</strong> {fallbackDesc.shadow}</p>}
            {fallbackDesc.readingThemes && fallbackDesc.readingThemes.length > 0 && (
              <p><strong>Lukutilanteissa:</strong> {fallbackDesc.readingThemes.join(', ')}</p>
            )}
            {fallbackDesc.reflection && <p className="tarot-course-reflection"><strong>Pohdintakysymys:</strong> {fallbackDesc.reflection}</p>}
          </div>
        )}
        {!content && !fallbackDesc && isMinor && (
          <div className="card-interpretation">
            <p className="card-meaning">Pienet arkanat – tulkinta kontekstin ja kysymyksen mukaan. Tarkastele kuvan symboleja ja omaa tilannettasi.</p>
          </div>
        )}
        {!content && !fallbackDesc && !isMinor && interp && (
          <div className="card-interpretation">
            {interp.description && <p className="card-meaning">{interp.description}</p>}
            {keywords && (
              <div className="card-keywords">
                <strong>Avainsanat:</strong> {keywords}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const SECTIONS_WITH_CARD_ACCORDION = ['major-arcana', 'minor-wands', 'minor-cups', 'minor-swords', 'minor-pentacles'];
const SUIT_BY_SECTION_ID = {
  'minor-wands': 'wands',
  'minor-cups': 'cups',
  'minor-swords': 'swords',
  'minor-pentacles': 'pentacles'
};

function getSectionCards(sectionId) {
  if (sectionId === 'major-arcana') return getMajorCards();
  const suit = SUIT_BY_SECTION_ID[sectionId];
  return suit ? getMinorCardsBySuit(suit) : [];
}

function getGridCardsForSection(sectionId, section) {
  const openCards = section?.practice?.openCards;
  const openBySuit = section?.practice?.openCardsBySuit;
  if (sectionId === 'major-arcana') {
    return getMajorCards().map(c => getDisplayCard(c));
  }
  if (openBySuit) {
    const suit = SUIT_BY_SECTION_ID[sectionId] || openBySuit;
    return getMinorCardsBySuit(suit).map(c => getDisplayCard(c));
  }
  if (openCards && (sectionId === 'tarot-cards' || sectionId === 'ritual-and-journal')) {
    return allCards.map(c => getDisplayCard(c));
  }
  return [];
}

function getSectionIdForCard(card) {
  if (!card) return null;
  if (card.arcana === 'major') return 'major-arcana';
  if (card.suit) return `minor-${card.suit}`;
  return null;
}

const TarotCoursePage = () => {
  const [currentSectionId, setCurrentSectionId] = useState('what-is-tarot');
  const [completedIds, setCompletedIds] = useState(() => (SHOW_PROGRESS ? loadProgress() : []));
  const [showCards, setShowCards] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [cardOrientation, setCardOrientation] = useState('upright');
  const [expandedCardKey, setExpandedCardKey] = useState(null);
  const [minorArcanaOpen, setMinorArcanaOpen] = useState(true);

  const openModalById = useCallback((id) => {
    const sourceCard = getCardById(id);
    if (!sourceCard) return;
    const sectionId = getSectionIdForCard(sourceCard);
    if (sectionId) setCurrentSectionId(sectionId);
    setSelectedCard(getDisplayCard(sourceCard));
    setSelectedCardId(id);
    setCardOrientation('upright');
    const pathname = window.location.pathname || '/courses/tarot';
    window.history.replaceState(undefined, '', `${pathname}#card-${id}`);
  }, []);

  const closeModal = useCallback(() => {
    setSelectedCard(null);
    setSelectedCardId(null);
    const pathname = window.location.pathname || '/courses/tarot';
    if (window.location.hash) window.history.replaceState(undefined, '', pathname);
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    const m = hash && hash.match(/^#card-(.+)$/);
    if (m) {
      const id = m[1];
      const sourceCard = getCardById(id);
      if (sourceCard) {
        setSelectedCard(getDisplayCard(sourceCard));
        setSelectedCardId(id);
        const sectionId = getSectionIdForCard(sourceCard);
        if (sectionId) setCurrentSectionId(sectionId);
      }
    }
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      if (!window.location.hash) {
        setSelectedCard(null);
        setSelectedCardId(null);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const toggleCompleted = useCallback((id) => {
    if (!SHOW_PROGRESS || !TAROT_COURSE_LEAF_IDS.includes(id)) return;
    setCompletedIds(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      saveProgress(next);
      return next;
    });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setExpandedCardKey(null);
  }, [currentSectionId]);

  const currentSection = tarotCourseSections[currentSectionId];
  const minorArcanaCompleted = SHOW_PROGRESS ? MINOR_ARCANA_CHILD_IDS.filter(id => completedIds.includes(id)).length : 0;
  const gridCards = currentSection ? getGridCardsForSection(currentSectionId, currentSection) : [];

  const renderNav = () => (
    <ul className="tarot-course-nav-list">
      {tarotCourseNav.map((item) => {
        if (item.type === 'group') {
          const isOpen = minorArcanaOpen;
          const hasActiveChild = item.children.some(c => c.id === currentSectionId);
          const isGroupActive = currentSectionId === item.id || hasActiveChild;
          return (
            <li key={item.id} className="tarot-course-nav-group">
              <button
                type="button"
                className={`tarot-course-nav-btn tarot-course-nav-group-btn ${isGroupActive ? 'active' : ''}`}
                onClick={() => {
                  setCurrentSectionId(item.id);
                  setMinorArcanaOpen(true);
                }}
                aria-expanded={isOpen}
                aria-controls={`nav-group-${item.id}`}
              >
                <span className="tarot-course-nav-title">{item.title}</span>
                {SHOW_PROGRESS && (
                  <span className="tarot-course-nav-group-badge" aria-hidden>
                    {minorArcanaCompleted}/{item.children.length}
                  </span>
                )}
                <span className="tarot-course-nav-chevron" aria-hidden>{isOpen ? '▼' : '▶'}</span>
              </button>
              <ul id={`nav-group-${item.id}`} className="tarot-course-nav-sublist" hidden={!isOpen}>
                {item.children.map((child) => (
                  <li key={child.id}>
                    <button
                      type="button"
                      className={`tarot-course-nav-btn tarot-course-nav-sub-btn ${currentSectionId === child.id ? 'active' : ''}`}
                      onClick={() => setCurrentSectionId(child.id)}
                    >
                      <span className="tarot-course-nav-title">{child.title}</span>
                      {SHOW_PROGRESS && completedIds.includes(child.id) && <span className="tarot-course-done" aria-hidden>✓</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          );
        }
        const isLeaf = TAROT_COURSE_LEAF_IDS.includes(item.id);
        return (
          <li key={item.id}>
            <button
              type="button"
              className={`tarot-course-nav-btn ${currentSectionId === item.id ? 'active' : ''}`}
              onClick={() => setCurrentSectionId(item.id)}
            >
              <span className="tarot-course-nav-title">{item.title}</span>
              {SHOW_PROGRESS && isLeaf && completedIds.includes(item.id) && <span className="tarot-course-done" aria-hidden>✓</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );

  const renderSectionContent = () => {
    if (!currentSection) return null;
    const section = currentSection;
    const withCardAccordion = SECTIONS_WITH_CARD_ACCORDION.includes(currentSectionId);
    const moduleCards = withCardAccordion ? getSectionCards(currentSectionId) : [];

    return (
      <>
        <div className="tarot-course-content">
          {section.content.map((block, i) => {
            if (typeof block !== 'object' || !block) {
              return <p key={i}>{block}</p>;
            }
            if (block.type === 'subheading') {
              return <h3 key={i} className="tarot-course-content-subheading">{block.text}</h3>;
            }
            if (block.type === 'list' && Array.isArray(block.items)) {
              return (
                <ul key={i} className="tarot-course-content-list">
                  {block.items.map((item, j) => (
                    <li key={j}>
                      {typeof item === 'object' && item != null && 'term' in item
                        ? <><strong>{item.term}</strong> {item.text}</>
                        : item
                      }
                    </li>
                  ))}
                </ul>
              );
            }
            if (block.type === 'lead' && block.text) {
              return <p key={i} className="tarot-course-content-lead">{block.text}</p>;
            }
            return <p key={i}>{typeof block === 'string' ? block : block.text || ''}</p>;
          })}
        </div>

        {section.groups && section.groups.length > 0 && (
          <div className="tarot-course-groups">
            {section.groups.map((grp, gi) => (
              <div key={gi} className="tarot-course-group card">
                <h4 className="tarot-course-group-title">{grp.title}</h4>
                <ul className="tarot-course-group-cues">
                  {grp.cues.map((cue, ci) => (
                    <li key={ci}>{cue}</li>
                  ))}
                </ul>
                {SHOW_EXERCISES && grp.exercise != null && (
                  <p className="tarot-course-group-exercise"><strong>Harjoitus:</strong> {grp.exercise}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {currentSectionId === 'classic-spreads' && section.spreads && section.spreads.length > 0 && (
          <div className="tarot-course-spreads">
            {section.spreads.map((spread, i) => (
              <div key={i} className="tarot-course-spread-card card">
                <div className="tarot-course-spread-content">
                  <h4 className="tarot-course-group-title">{spread.name}</h4>
                  {spread.positions && spread.positions.length > 0 && (
                    <ul className="tarot-course-group-cues">
                      {spread.positions.map((pos, j) => (
                        <li key={j}>{pos}</li>
                      ))}
                    </ul>
                  )}
                  <p className="tarot-course-group-exercise">{spread.instructions}</p>
                  {spread.exampleQuestions && spread.exampleQuestions.length > 0 && (
                    <p className="tarot-course-spread-questions">
                      <strong>Esimerkkikysymyksiä:</strong> {spread.exampleQuestions.join(' — ')}
                    </p>
                  )}
                </div>
                <div className="tarot-course-spread-diagram">
                  <SpreadDiagramByIndex index={i} />
                </div>
              </div>
            ))}
            {SHOW_EXERCISES && section.practice && (
              <div className="tarot-course-practice card">
                <h3 className="tarot-course-practice-label">{section.practice.label}</h3>
                <p className="tarot-course-practice-text">{section.practice.text}</p>
              </div>
            )}
          </div>
        )}

        {withCardAccordion && (
          <div className="tarot-course-card-list-wrap">
            <h3 className="tarot-course-accordion-title">Kortit</h3>
            <ul className="tarot-course-card-row-list" aria-label="Kortit">
              {moduleCards.map((c) => {
                const key = c.id;
                const displayCard = getDisplayCard(c);
                const isMajor = c.arcana === 'major';
                const listTitle = isMajor
                  ? formatMajorArcanaTitle(c.number, c.name.fi, c.name.en)
                  : formatMinorArcanaTitle(c.suit, c.rankKey, c.name.en);
                return (
                  <li key={key} id={`card-${key}`} className="tarot-course-card-row">
                    <div className="tarot-course-card-row-thumb-wrap">
                      {displayCard && displayCard.imageUrl && (
                        <img src={displayCard.imageUrl} alt="" className="tarot-course-card-row-thumb" />
                      )}
                    </div>
                    <button
                      type="button"
                      className="tarot-course-card-row-btn"
                      onClick={() => openModalById(key)}
                      aria-label={`Avaa ${listTitle}`}
                    >
                      <span className="tarot-course-card-row-title">
                        {listTitle}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary tarot-course-card-row-open"
                      onClick={() => openModalById(key)}
                    >
                      Näytä kortti
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {SHOW_EXERCISES && section.practice && currentSectionId !== 'classic-spreads' && (
          <div className="tarot-course-practice card">
            <h3 className="tarot-course-practice-label">{section.practice.label}</h3>
            <p className="tarot-course-practice-text">{section.practice.text}</p>
            {(section.practice.openCards || section.practice.openCardsBySuit) && (
              <div className="tarot-course-practice-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowCards(!showCards)}
                >
                  {showCards
                    ? 'Piilota kortit'
                    : section.practice.openCardsBySuit
                      ? 'Avaa tämän maan kortit'
                      : 'Avaa kortit'}
                </button>
                <Link to="/tarot" className="btn btn-secondary">Nosta kortti</Link>
              </div>
            )}
          </div>
        )}

        {SHOW_EXERCISES && showCards && gridCards.length > 0 && (
          <div className="tarot-course-card-grid-wrap">
            <h3 className="tarot-course-grid-title">
              {currentSectionId === 'major-arcana'
                ? 'Suuret arkanat'
                : SUIT_BY_SECTION_ID[currentSectionId]
                  ? `Valitse kortti (${SUIT_BY_SECTION_ID[currentSectionId] === 'wands' ? 'Sauvat' : SUIT_BY_SECTION_ID[currentSectionId] === 'cups' ? 'Maljat' : SUIT_BY_SECTION_ID[currentSectionId] === 'swords' ? 'Miekat' : 'Lantit'})`
                  : 'Valitse kortti'}
            </h3>
            <div className="tarot-card-display tarot-course-grid">
              {gridCards.map((c) => (
                <button
                  key={c?.isMinor ? `${c.suit}-${c.rankKey}` : c?.name || c?.nameFi}
                  type="button"
                  className="tarot-course-card-thumb"
                    onClick={() => {
                    const id = c?.isMinor ? `${c.suit}-${c.rankKey}` : (c?.name && c.name.toLowerCase().replace(/\s+/g, '-'));
                    if (id) openModalById(id);
                  }}
                  aria-label={`Avaa ${c?.isMinor ? formatMinorArcanaTitle(c.suit, c.rankKey, c.name) : (c?.number != null && c?.name ? formatMajorArcanaTitle(c.number, c.nameFi, c.name) : c?.nameFi || '')}`}
                >
                  {c?.imageUrl && (
                    <img
                      src={c.imageUrl}
                      alt=""
                      className="tarot-card-image"
                    />
                  )}
                  <span className="tarot-course-card-name">
                    {c?.isMinor ? formatMinorArcanaTitle(c.suit, c.rankKey, c.name) : (c?.number != null && c?.name ? formatMajorArcanaTitle(c.number, c.nameFi, c.name) : c?.nameFi || '')}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {SHOW_PROGRESS && TAROT_COURSE_LEAF_IDS.includes(currentSectionId) && (
          <div className="tarot-course-done-wrap">
            <label className="tarot-course-checkbox-label">
              <input
                type="checkbox"
                checked={completedIds.includes(currentSectionId)}
                onChange={() => toggleCompleted(currentSectionId)}
              />
              <span>Merkitse moduuli valmiiksi</span>
            </label>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="tarot-course-page">
      <div className="tarot-course-layout">
        <nav className="tarot-course-nav" aria-label="Kurssimoduulit">
          {renderNav()}
        </nav>

        <main className="tarot-course-main">
          {currentSectionId === 'what-is-tarot' && (
            <header className="tarot-course-header">
              <h1 className="tarot-course-title">{TAROT_COURSE_TITLE}</h1>
              <p className="tarot-course-intro">{TAROT_COURSE_INTRO}</p>
            </header>
          )}

          {currentSection && (
            <section className="tarot-course-module" aria-labelledby={`module-title-${currentSectionId}`}>
              <h2 id={`module-title-${currentSectionId}`} className="tarot-course-module-title">
                {currentSection.title}
              </h2>
              {renderSectionContent()}
            </section>
          )}
        </main>
      </div>

      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          orientation={cardOrientation}
          onClose={closeModal}
          generatedCard={selectedCardId ? getGeneratedCardById(selectedCardId) : null}
        />
      )}
    </div>
  );
};

export default TarotCoursePage;
